import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/feedback/Skeleton";
import { PurchaseInvoiceCard } from "@/features/purchaseInvoices/PurchaseInvoiceCard";
import {
  getPurchaseInvoice,
  purchaseInvoiceKeys,
} from "@/features/purchaseInvoices/purchaseInvoices.api";
import { firstAllowedRoute, hasModule } from "@/lib/auth";

// A purchase invoice: the pdf beside what it says and how it is coded, and what AFAS answered (docs/inkoopfacturen.md).
export const Route = createFileRoute("/_shell/purchase-invoices/$id")({
  beforeLoad: ({ context }) => {
    const { session } = context;
    if (
      !session.roles.includes("admin") &&
      !hasModule(session, "purchase_invoices") &&
      !hasModule(session, "purchase_invoices_write")
    ) {
      throw redirect({ to: firstAllowedRoute(session) });
    }
  },
  component: PurchaseInvoiceScreen,
});

function PurchaseInvoiceScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { session } = Route.useRouteContext();
  const canWrite =
    session.roles.includes("admin") ||
    hasModule(session, "purchase_invoices_write");
  const query = useQuery({
    queryKey: purchaseInvoiceKeys.detail(id),
    queryFn: () => getPurchaseInvoice(id),
    retry: false,
    // While it is on its way to AFAS or being read, every five seconds: the answer comes by itself.
    refetchInterval: (q) =>
      q.state.data?.status === "queued" ||
      q.state.data?.readStatus === "pending"
        ? 5_000
        : false,
  });

  if (query.isPending) {
    return (
      <div
        className="wl-page"
        data-screen-label="Back-office Purchase Invoice"
        aria-busy="true"
      >
        <Skeleton width="280px" height="var(--space-7)" />
        <div style={{ marginTop: "var(--space-5)" }}>
          <Skeleton height="480px" />
        </div>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Purchase Invoice">
        <h1 className="t-h2">{t("purchaseInvoices.notFound")}</h1>
      </div>
    );
  }
  return (
    <div className="wl-page" data-screen-label="Back-office Purchase Invoice">
      <PurchaseInvoiceCard invoice={query.data} canWrite={canWrite} />
    </div>
  );
}
